import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

/** Roles allowed to create/deactivate receipt upload links (backend enforces the same list). */
export const RECEIPT_LINK_ROLES = ['accountant', 'company_admin', 'fleet_owner', 'platform_admin'];

const linkUrl = (token) => `${window.location.origin}/receipt-upload/${token}`;

/**
 * One permanent receipt-upload link per driver. Links never expire and have no upload limit;
 * the backend flags duplicate uploads in the vault instead.
 */
const ReceiptLinks = ({ BACKEND_URL, fetchWithAuth }) => {
  const { user } = useAuth();
  const canManage = RECEIPT_LINK_ROLES.includes(user?.role);
  const [links, setLinks] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [driverId, setDriverId] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/accounting/shareable-links`);
      if (res.ok) setLinks((await res.json()).links || []);
    } catch (err) {
      console.error('Error loading receipt links:', err);
    }
  };

  useEffect(() => {
    if (!canManage) return;
    load();
    fetchWithAuth(`${BACKEND_URL}/api/drivers/all`)
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => setDrivers((Array.isArray(list) ? list : []).map((d) => ({ id: d.id, name: d.full_name || d.name || d.email || d.id }))))
      .catch(() => {});
  }, [canManage, BACKEND_URL]);

  const active = links.filter((l) => l.status !== 'deactivated');
  const linkedDriverIds = useMemo(() => new Set(active.map((l) => l.driver_id).filter(Boolean)), [active]);
  const available = drivers.filter((d) => !linkedDriverIds.has(d.id));

  if (!canManage) return null;

  const create = async () => {
    const driver = drivers.find((d) => d.id === driverId);
    if (!driver) return;
    setBusy(true);
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/accounting/shareable-links`, {
        method: 'POST',
        body: JSON.stringify({ driver_id: driver.id, driver_name: driver.name, submitter_name: driver.name, label: `Receipts — ${driver.name}` }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`);
      const link = await res.json();
      await navigator.clipboard?.writeText(linkUrl(link.token)).catch(() => {});
      toast.success(`Link created for ${driver.name} and copied`);
      setDriverId('');
      load();
    } catch (err) {
      toast.error(`Could not create link: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const copy = async (token) => {
    try {
      await navigator.clipboard.writeText(linkUrl(token));
      toast.success('Link copied');
    } catch {
      toast.error(linkUrl(token));
    }
  };

  const deactivate = async (link) => {
    if (!window.confirm(`Deactivate ${link.driver_name || link.label}'s upload link? The driver will no longer be able to send receipts with it.`)) return;
    const res = await fetchWithAuth(`${BACKEND_URL}/api/accounting/shareable-links/${link.token}`, { method: 'DELETE' }).catch(() => null);
    if (res?.ok) {
      toast.success('Link deactivated');
      load();
    } else toast.error('Could not deactivate the link');
  };

  return (
    <Card className="border border-border shadow-sm">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="text-lg flex items-center text-foreground">
              <i className="fas fa-link text-sky-500 mr-2"></i>
              Driver Receipt Upload Links ({active.length})
            </CardTitle>
            <p className="text-sm text-muted-foreground">One permanent link per driver — no expiry, no upload limit. Duplicate uploads are flagged in the vault.</p>
          </div>
          <div className="flex gap-2">
            <select
              id="receipt-link-driver"
              value={driverId}
              onChange={(e) => setDriverId(e.target.value)}
              className="bg-background border border-border rounded text-sm h-9 px-2 min-w-[200px]"
            >
              <option value="">{available.length ? 'Select driver…' : 'All drivers have a link'}</option>
              {available.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
            <Button id="receipt-link-create" disabled={!driverId || busy} onClick={create}>
              <i className="fas fa-plus mr-2"></i>Create link
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {active.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">No links yet. Create one per driver and send it to them once.</p>
        ) : (
          <div className="divide-y divide-border">
            {active.map((l) => (
              <div key={l.token} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <div>
                  <span className="font-medium">{l.driver_name || l.submitter_name || l.label}</span>
                  <span className="text-xs text-muted-foreground ml-2">created {new Date(l.created_at).toLocaleDateString('en-CA')}</span>
                  <Badge variant="outline" className="ml-2 text-[10px]">
                    {l.submissions_count || 0} uploads
                  </Badge>
                </div>
                <div className="flex gap-2">
                  <Button id={`receipt-link-copy-${l.token}`} size="sm" variant="outline" onClick={() => copy(l.token)}>
                    <i className="fas fa-copy mr-2"></i>Copy link
                  </Button>
                  <Button id={`receipt-link-deactivate-${l.token}`} size="sm" variant="ghost" className="text-red-500" onClick={() => deactivate(l)}>
                    Deactivate
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ReceiptLinks;
